import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  Stack,
  Tab,
  Tabs,
  Text,
  TextField,
} from '@origin-digital/ods-core';
import { Autocomplete } from '@origin-digital/ods-lab';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { get } from 'lodash';
import { matchSorter } from 'match-sorter';
import styled from 'styled-components';
import { AddressMIRNSearch } from '../AddressMeterSearch/AddressMIRNSearch';
import { AddressNMISearch } from '../AddressMeterSearch/AddressNMISearch';
import {
  generateDialogContent,
  useAddressTypeConfig,
} from '../AddressMeterSearch/useAddressTypeConfig';
import { IOriginAddressMeter } from '../fetchers';
import { call, createSafeLabel, getOptionLabel } from '../helpers';
import { content } from './content';
import { AddressEntryValidator, reduceValidators } from './helpers';
import { useAutocompleteState, useTextFieldState } from './hooks';
import { metadata } from './metadata';
import {
  AddressManualEntryField,
  AddressManualEntryValues,
  AddressSearchTabConfig,
  AddressTypes,
} from './models';
import {
  State,
  stateOptions,
  streetTypeOptions,
  streetTypeSuffixOptions,
  UnitType,
  unitTypeOptions,
} from './options';
import { ConnectionAddressObject } from '../models';

// Issue with ODS-2 Tab where the inactive tab font weight too light
const StyledTab = styled(Tab)`
  font-weight: normal;
  :active {
    font-weight: 600;
  }
`;

export interface AddressManualEntryProps {
  onSubmit?: (values: AddressManualEntryValues, errors: AddressManualEntryError[]) => void;
  onChange?: (
    field: AddressManualEntryField,
    value: string,
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  id: string;
  'data-id'?: string;
  loading?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  optionalStreetType?: boolean;
}

export interface AddressManualEntryError {
  field: AddressManualEntryField;
  error: string;
}

export interface AddressManualEntryComponentProps extends AddressManualEntryProps {
  isOpen: boolean;
  defaultTab?: number;
  onClose?: () => void;
}

export interface AddressManualEntryDialogProps extends AddressManualEntryComponentProps {
  title?: string;
  content?: (
    searchByNmiEnabled?: boolean,
    searchByMirnEnabled?: boolean,
    searchTabs?: AddressSearchTabConfig[]
  ) => React.ReactNode;
  searchByNmi?: {
    enabled: boolean;
    endpoint: string;
    onConfirm: (address: IOriginAddressMeter | ConnectionAddressObject) => void;
  };
  searchByMirn?: {
    enabled: boolean;
    endpoint: string;
    onConfirm: (address: IOriginAddressMeter | ConnectionAddressObject) => void;
  };
  searchTabs?: AddressSearchTabConfig[];
}

export const AddressManualEntry = ({
  onSubmit,
  onChange,
  disabled,
  loading,
  id,
  errorMessage,
  optionalStreetType,
  ...props
}: AddressManualEntryProps) => {
  useEffect(() => {
    if (optionalStreetType && metadata?.streetType) {
      metadata.streetType.validators = [];
    }
  }, [optionalStreetType]);

  const fieldStates: Record<AddressManualEntryField, any> = {
    unitType: useAutocompleteState<UnitType>(),
    unitNumber: useTextFieldState(),
    allotment: useTextFieldState(),
    streetNumber: useTextFieldState(),
    streetName: useTextFieldState(),
    streetType: useAutocompleteState(),
    streetTypeSuffix: useAutocompleteState(),
    city: useTextFieldState(),
    state: useAutocompleteState<State>(),
    postcode: useTextFieldState(),
  };

  const [submitCount, setSubmitCount] = useState<number>(0);

  const dataId = props['data-id'] || id;

  const handleValidate = (field: AddressManualEntryField) => () => {
    const { validators } = metadata[field];
    const { value } = fieldStates[field];
    const error = reduceValidators(value, getValues(), validators as AddressEntryValidator[]);

    fieldStates[field].setError(error);

    return error;
  };

  const handleValidateOnDependentFields = (fields: AddressManualEntryField[]) => () => {
    fields.forEach((key) => {
      handleValidate(key)();
    });
  };

  const getBaseProps = (field: AddressManualEntryField) => {
    const { label } = metadata[field];

    const dependentFields = ['allotment', 'streetNumber'] as AddressManualEntryField[];

    return {
      disabled: disabled || loading,
      label: createSafeLabel(label),
      id: `${id}-${field}`,
      'data-id': `${dataId}-${field}`,
      ...fieldStates[field],
      onBlur:
        submitCount > 0 &&
        (dependentFields.includes(field)
          ? handleValidateOnDependentFields(dependentFields)
          : handleValidate(field)),
      onChange: handleChange(field),
      domProps: {
        autoComplete: 'disable',
      },
    };
  };

  const filterOptions = (
    options: { label: string; value: string }[],
    params: FilterOptionsState<{ label: string; value: string }>
  ) => {
    return matchSorter(options, params.inputValue, {
      keys: ['label', 'value'],
      threshold: matchSorter.rankings.STARTS_WITH,
    });
  };

  const getAutocompleteProps = (field: AddressManualEntryField) => {
    return {
      muiProps: {
        autoHighlight: true,
        filterOptions,
      },
      textFieldProps: {
        'data-id': `${dataId}-${field}-text-field`,
        ...fieldStates[field],
      },
    };
  };

  const getValues = (): AddressManualEntryValues => {
    const values: AddressManualEntryValues = { ...fieldStates };

    for (const key in fieldStates) {
      const field = key as AddressManualEntryField;

      values[field] = ['unitType', 'state', 'streetType', 'streetTypeSuffix'].includes(field)
        ? get(fieldStates[field], 'value.value', '')
        : fieldStates[field].value;
    }

    return values;
  };

  const handleChange =
    (field: AddressManualEntryField) =>
    (event: ChangeEvent<HTMLInputElement>, newValue?: string) => {
      fieldStates[field].onChange(event, newValue);
      call(onChange, field, event.target.value, event);
    };

  const handleSubmit = () => {
    setSubmitCount(submitCount + 1);

    const errors: { field: AddressManualEntryField; error: string }[] = [];
    const values = getValues();

    for (const key in fieldStates) {
      const field = key as AddressManualEntryField;
      const error = handleValidate(field)();

      error &&
        errors.push({
          field,
          error,
        });
    }

    call(onSubmit, values, errors);
  };

  return (
    <Box id={id} data-id={dataId}>
      <Stack space="medium">
        <Columns collapseBelow="sm" space={['medium', 'large']}>
          <Column width="1/3">
            <TextField {...getBaseProps('allotment')} />
          </Column>
          <Column width="1/3">
            <TextField {...getBaseProps('unitNumber')} />
          </Column>
          <Column width="1/3">
            <TextField
              {...getBaseProps('streetNumber')}
              required={!fieldStates['allotment'].value.length}
            />
          </Column>
        </Columns>

        <Autocomplete<UnitType>
          options={unitTypeOptions}
          getOptionLabel={getOptionLabel}
          {...getBaseProps('unitType')}
          {...getAutocompleteProps('unitType')}
        />

        <TextField {...getBaseProps('streetName')} required />

        <Autocomplete
          options={streetTypeOptions}
          getOptionLabel={getOptionLabel}
          {...getBaseProps('streetType')}
          {...getAutocompleteProps('streetType')}
          helperText="(e.g. Road, Drive, Terrace etc.)"
          textFieldProps={{ required: !optionalStreetType }}
        />

        <Autocomplete
          options={streetTypeSuffixOptions}
          getOptionLabel={getOptionLabel}
          {...getBaseProps('streetTypeSuffix')}
          {...getAutocompleteProps('streetTypeSuffix')}
          helperText="If applicable (e.g. South)"
        />

        <Columns collapseBelow="sm" space={['medium', 'large']}>
          <Column width="1/3">
            <TextField {...getBaseProps('city')} required />
          </Column>
          <Column width="1/3">
            <Autocomplete<State>
              options={stateOptions}
              {...getBaseProps('state')}
              {...getAutocompleteProps('state')}
              textFieldProps={{ required: true }}
            />
          </Column>
          <Column width="1/3">
            <TextField {...getBaseProps('postcode')} maxLength={4} required />
          </Column>
        </Columns>

        {errorMessage && (
          <Alert tone="critical" data-id="address-error">
            <Text>{errorMessage}</Text>
          </Alert>
        )}

        <Button
          disabled={disabled}
          data-id={`${dataId}-button`}
          onClick={handleSubmit}
          spinner={loading}
        >
          {content.button.label}
        </Button>
      </Stack>
    </Box>
  );
};

export const AddressManualEntryDefault = ({
  isOpen,
  ...props
}: AddressManualEntryComponentProps) => {
  if (!isOpen) return null;
  return <AddressManualEntry {...props} />;
};

export const AddressManualEntryDialog = ({
  isOpen,
  onClose,
  defaultTab = 0,
  title = 'Find your address',
  content = (searchByNmiEnabled, searchByMirnEnabled, searchTabs) => {
    const enabledTypes = searchTabs
      ? searchTabs.map((tab) => tab.type)
      : [
          ...(searchByNmiEnabled ? [AddressTypes.NMI] : []),
          ...(searchByMirnEnabled ? [AddressTypes.MIRN] : []),
        ];

    return (
      <Box marginBottom="large">
        <Text>{generateDialogContent(enabledTypes)}</Text>
      </Box>
    );
  },
  searchByNmi,
  searchByMirn,
  searchTabs,
  ...props
}: AddressManualEntryDialogProps) => {
  const ADDRESS_TYPE_CONFIGS = useAddressTypeConfig();
  const tabsEnabled =
    searchByNmi?.enabled || searchByMirn?.enabled || (searchTabs && searchTabs.length > 0);

  return (
    <Dialog open={isOpen} onClose={onClose} id={`${props.id}-dialog`} title={title}>
      {content(searchByNmi?.enabled, searchByMirn?.enabled, searchTabs)}
      {tabsEnabled ? (
        <Tabs defaultValue={defaultTab}>
          <StyledTab label="Street address" id="street-address-tab">
            <AddressManualEntry {...props} />
          </StyledTab>
          {[
            searchByNmi?.enabled && !searchTabs && (
              <StyledTab label="NMI number" id="nmi-search-tab">
                <Stack space="medium">
                  <AddressNMISearch
                    endpoint={searchByNmi.endpoint}
                    onConfirm={(address) => searchByNmi.onConfirm(address)}
                  />
                </Stack>
              </StyledTab>
            ),
            searchByMirn?.enabled && !searchTabs && (
              <StyledTab label="MIRN number" id="mirn-search-tab">
                <Stack space="medium">
                  <AddressMIRNSearch
                    endpoint={searchByMirn.endpoint}
                    onConfirm={(address) => searchByMirn.onConfirm(address)}
                  />
                </Stack>
              </StyledTab>
            ),
            ...(searchTabs && searchTabs.length > 0
              ? searchTabs.map(({ type, endpoint, onConfirm }) => {
                  const tabMeta = ADDRESS_TYPE_CONFIGS[type];

                  if (!tabMeta) return null;

                  const Component = tabMeta.searchComponent;

                  return (
                    <StyledTab key={type} label={tabMeta.label} id={`${type}-search-tab`}>
                      <Stack space="medium">
                        <Component endpoint={endpoint} onConfirm={onConfirm} />
                      </Stack>
                    </StyledTab>
                  );
                })
              : []),
          ].filter(Boolean)}
        </Tabs>
      ) : (
        <AddressManualEntry {...props} />
      )}
    </Dialog>
  );
};
